import { create } from 'zustand';
import dayjs from 'dayjs';
import { LIMITED_ACTIVITY_TYPE } from 'lib/constants/common';

/**
 * 限时活动类型
 * - newUserOnly: 仅新用户，只在活动期间订购才计算月数，从订购时间算起
 * - supportOldUser: 支持老用户，活动前订购也可以，从订购时间算起
 * - latestTime: 从最晚时间计算，取活动开始时间和订购时间中较晚的那个来计算月份差
 */
export type LimitedActivityType = 'newUserOnly' | 'supportOldUser' | 'latestTime';

interface LimitedActivityStore {
  limitedActivityType: LimitedActivityType | null;
  limitedTimeActivityDateRange?: {
    start: string;
    end: string;
  };
  // 已订购用户持续月数
  durationInMonths: number;
  initData: (params: { dateRange?: { start: string; end: string }; limitedActivityType?: LimitedActivityType }) => void;
  updateDurationInMonths: (params: {
    activityStartTime?: string;
    activityEndTime?: string;
    limitedActivityType: LimitedActivityType | null;
    packageOrderTime?: string;
    isSubscriber?: boolean;
  }) => void;
}

export const createLimitedActivityStore = () =>
  create<LimitedActivityStore>((set) => ({
    limitedActivityType: null,
    limitedTimeActivityDateRange: undefined,
    durationInMonths: 0,
    initData: (params) =>
      set({
        limitedTimeActivityDateRange: params.dateRange,
        limitedActivityType: params.limitedActivityType,
      }),
    updateDurationInMonths: (params) => {
      const { activityStartTime, activityEndTime, limitedActivityType, packageOrderTime, isSubscriber } = params;

      // 如果不是订购用户，或者活动开始时间或订购时间无效，则持续月数为0
      if (!isSubscriber || !activityStartTime || !activityEndTime || !packageOrderTime) {
        set({ durationInMonths: 0 });
        return;
      }

      const orderDate = dayjs(packageOrderTime);
      const startDate = dayjs(activityStartTime).startOf('day');
      const endDate = dayjs(activityEndTime).endOf('day');
      const currentDate = dayjs();

      if (!startDate.isValid() || !endDate.isValid() || !orderDate.isValid()) {
        set({ durationInMonths: 0 });
        return;
      }

      // 计算两个日期之间的月份差（订购当月算第1个月）
      const calculateMonthsDiff = (fromDate: dayjs.Dayjs, toDate: dayjs.Dayjs = currentDate) => {
        const fromMonth = fromDate.startOf('month');
        const toMonth = toDate.startOf('month');
        return toMonth.diff(fromMonth, 'month') + 1;
      };

      // 检查订购时间是否在活动时间范围内
      const isOrderInActivityRange = !(orderDate.isBefore(startDate) || orderDate.isAfter(endDate));

      switch (limitedActivityType) {
        case LIMITED_ACTIVITY_TYPE.USER_ORDER_TIME_NEW: {
          // 仅新用户：只有在活动期间订购才计算月数，从订购时间算起
          if (!isOrderInActivityRange) {
            set({ durationInMonths: 0 });
            return;
          }
          set({ durationInMonths: calculateMonthsDiff(orderDate) });
          break;
        }

        case LIMITED_ACTIVITY_TYPE.USER_ORDER_TIME_OLD: {
          // 支持老用户：活动前订购也可以，从订购时间算起
          if (orderDate.isAfter(endDate)) {
            // 订购时间晚于活动结束时间
            set({ durationInMonths: 0 });
            return;
          }
          set({ durationInMonths: calculateMonthsDiff(orderDate) });
          break;
        }

        case LIMITED_ACTIVITY_TYPE.LATEST_TIME: {
          // 从最晚时间计算：取活动开始时间和订购时间中较晚的那个来计算月份差
          if (orderDate.isAfter(endDate)) {
            // 订购时间晚于活动结束时间
            set({ durationInMonths: 0 });
            return;
          }
          // 取订购时间和活动开始时间中较晚的那个
          const latestDate = orderDate.isAfter(startDate) ? orderDate : startDate;
          set({ durationInMonths: calculateMonthsDiff(latestDate) });
          break;
        }

        default:
          set({ durationInMonths: 0 });
      }
    },
  }));
